import { useState, useEffect, useCallback, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { getGravatarUrl } from '../service/api/gravatar';
import { getAccountImageUrl } from '../service/api/accountImageService';

// 🆕 CACHE GLOBAL DES IMAGES (singleton)
const imageCache = new Map();
const loadingCache = new Set();


const ImageWithApi = ({ 
  accountId,
  accountType,
  email, 
  alt, 
  className, 
  imageType = 'auto',
  fallbackToGravatar = true,
  ...props 
}) => {
  const cacheKey = `${accountId}-${imageType}-${accountType}`;
  const [imageUrl, setImageUrl] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [error, setError] = useState(false);

  //  Vérifier immédiatement si image en cache
  const cachedImage = useMemo(() => {
    if (imageCache.has(cacheKey)) {
      return imageCache.get(cacheKey);
    }
    return null;
  }, [cacheKey]);

  useEffect(() => {

    // IMMÉDIATEMENT : Si déjà en cache, afficher instantanément
    if (cachedImage) {
      setImageUrl(cachedImage);
      setIsCached(true);
      return;
    }

    // S'il est en chargement, juste attendre
    if (loadingCache.has(cacheKey)) {
      return;
    }

    const fetchImage = async () => {
      loadingCache.add(cacheKey);
      
      try {

        if (!accountId) {
          if (fallbackToGravatar && email) {
            const gravatarUrl = getGravatarUrl(email);
            imageCache.set(cacheKey, gravatarUrl);
            setImageUrl(gravatarUrl);
          }
          return;
        }

        let targetImageType = imageType;
        if (imageType === 'auto') {
          targetImageType = ['Entreprise', 'Banques', 'Assurance', 'Autres Entreprises', 'Association','autres','Opc','Opcvm','Fia','Assurance','Issuer']
            .includes(accountType) ? 'LOGO' : 'PICTURE';
        }

        const url = await getAccountImageUrl(accountId, targetImageType);        
        if (url) {
          // STOCKER EN CACHE GLOBAL
          imageCache.set(cacheKey, url);
          setImageUrl(url);
          setError(false);
        } else {
          throw new Error('No image');
        }
      } catch (error) {
        setError(true);
        if (fallbackToGravatar && email) {
          const gravatarUrl = getGravatarUrl(email);
          imageCache.set(cacheKey, gravatarUrl);
          setImageUrl(gravatarUrl);
        }
      } finally {
        loadingCache.delete(cacheKey);
      }
    };

    fetchImage();
  }, [accountId, accountType, email, imageType, fallbackToGravatar, cacheKey, cachedImage]);

  const handleImageError = useCallback((e) => {
    setError(true);
    if (fallbackToGravatar && email && e.target.src !== getGravatarUrl(email)) {
      const gravatarUrl = getGravatarUrl(email);
      imageCache.set(cacheKey, gravatarUrl);
      e.target.src = gravatarUrl;
    }
  }, [cacheKey, email, fallbackToGravatar]);

  // SKELETON PROFESSIONNEL si pas encore chargé OU en cache loading
  if (!imageUrl || loadingCache.has(cacheKey)) {
    return (
      <div className={`${className} position-relative overflow-hidden`}
           style={{ 
             width: '50px', 
             height: '50px',
             borderRadius: '50%',
             background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
             backgroundSize: '200% 100%',
             animation: 'skeleton-loading 1.5s infinite'
           }}>
        <div className="position-absolute top-50 start-50 translate-middle">
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.3)',
            animation: 'skeleton-pulse 1.2s infinite'
          }} />
        </div>
      </div>
    );
  }

  return (
    <img 
      src={imageUrl} 
      alt={alt} 
      className={className} 
      onError={handleImageError}
      loading="lazy"
      style={{ 
        objectFit: 'contain',      
        width: '100%',             
        height: '100%',            
        borderRadius: '50%',
        backgroundColor: '#f3f4f6' 
      }}
      {...props} 
    />
  );
};

export default ImageWithApi;
